import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';

export interface SignalEvent {
  id: string;
  bot_id: string;
  signal_token: string;
  action: string;
  instrument: string;
  timestamp: string;
  created_at: string;
  forward_logs: SignalForwardLog[];
  bot?: {
    name: string;
  }[] | null;
}

export interface SignalForwardLog {
  id: string;
  event_id: string;
  success: boolean;
  duration_ms: number | null;
  error_message: string | null;
  created_at: string;
  endpoint?: {
    name: string;
    bot?: {
      name: string;
    }[] | null;
  }[] | null;
}

export interface MonitoringStats {
  totalSignals: number;
  successfulForwards: number;
  failedForwards: number;
  averageResponseTime: number;
  successRate: number;
}

export const useBotMergeMonitoring = (timeframe: 'today' | '7d' | '30d' = 'today') => {
  const getDateRange = () => {
    const now = new Date();
    switch (timeframe) {
      case 'today':
        return { start: startOfDay(now), end: endOfDay(now) };
      case '7d':
        return { start: startOfDay(subDays(now, 7)), end: endOfDay(now) };
      case '30d':
        return { start: startOfDay(subDays(now, 30)), end: endOfDay(now) };
      default:
        return { start: startOfDay(now), end: endOfDay(now) };
    }
  };

  const { start, end } = getDateRange();

  // Real-time signal events with forward logs
  const { data: signalEvents = [], isLoading: eventsLoading, refetch: refetchEvents } = useQuery({
    queryKey: ['bot-merge-signal-events', timeframe],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('bot_signal_events')
        .select(`
          id,
          bot_id,
          signal_token,
          action,
          instrument,
          timestamp,
          created_at,
          bot:bot_id (
            name
          )
        `)
        .gte('created_at', start.toISOString())
        .lte('created_at', end.toISOString())
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      console.log('Raw signal events data:', data);
      return data || [];
    },
    refetchInterval: 5000, // Auto-refresh every 5 seconds
  });

  // Forward logs for the signal events
  const { data: forwardLogs = [], isLoading: logsLoading } = useQuery({
    queryKey: ['bot-merge-forward-logs', timeframe, signalEvents.map(e => e.id).join(',')],
    queryFn: async () => {
      if (signalEvents.length === 0) return [];

      const { data, error } = await supabase
        .from('bot_signal_forward_logs')
        .select(`
          id,
          event_id,
          success,
          duration_ms,
          error_message,
          created_at,
          endpoint:endpoint_id (
            name,
            bot:bot_id (
              name
            )
          )
        `)
        .in('event_id', signalEvents.map(e => e.id))
        .order('created_at', { ascending: false });

      if (error) throw error;
      console.log('Raw forward logs data:', data);
      return data || [];
    },
    enabled: signalEvents.length > 0,
  });

  // Monitoring statistics
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['bot-merge-monitoring-stats', timeframe],
    queryFn: async (): Promise<MonitoringStats> => {
      const { data: logStats, error } = await supabase
        .from('bot_signal_forward_logs')
        .select('success, duration_ms')
        .gte('created_at', start.toISOString())
        .lte('created_at', end.toISOString());

      if (error) throw error;

      const totalSignals = signalEvents.length;
      const successfulForwards = logStats?.filter(log => log.success === true).length || 0;
      const failedForwards = logStats?.filter(log => log.success === false).length || 0;
      const totalForwards = successfulForwards + failedForwards;
      
      const responseTimes = logStats?.filter(log => 
        log.success === true && log.duration_ms !== null
      ).map(log => log.duration_ms!) || [];
      
      const averageResponseTime = responseTimes.length > 0 
        ? responseTimes.reduce((sum, time) => sum + time, 0) / responseTimes.length 
        : 0;

      const successRate = totalForwards > 0 ? (successfulForwards / totalForwards) * 100 : 0;

      return {
        totalSignals,
        successfulForwards,
        failedForwards,
        averageResponseTime: Math.round(averageResponseTime),
        successRate: Math.round(successRate * 100) / 100,
      };
    },
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  // Combine signal events with their forward logs
  const enrichedSignalEvents: SignalEvent[] = signalEvents.map(event => ({
    ...event,
    forward_logs: forwardLogs.filter(log => log.event_id === event.id),
  }));

  return {
    signalEvents: enrichedSignalEvents,
    stats: stats || {
      totalSignals: 0,
      successfulForwards: 0,
      failedForwards: 0,
      averageResponseTime: 0,
      successRate: 0,
    },
    isLoading: eventsLoading || logsLoading || statsLoading,
    refetch: () => {
      refetchEvents();
    },
  };
};